import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  FiArrowLeft, FiEdit2, FiSave, FiX, FiUser, FiBriefcase,
  FiMail, FiPhone, FiMapPin, FiCalendar, FiDollarSign,
  FiFileText, FiImage, FiCreditCard, FiCheck, FiDownload,
  FiChevronRight, FiTag
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('reservations');
  
  const [formData, setFormData] = useState({
    email: '', first_name: '', last_name: '', phone: '',
    address: '', city: '', postal_code: '', tags: [], notes: '',
    marketing_consent: false, client_type: 'particulier',
    company_name: '', siret: ''
  });

  useEffect(() => {
    if (customerId && customerId !== 'new') {
      loadCustomer();
    } else {
      setLoading(false);
      setEditing(true);
    }
  }, [customerId]);

  const loadCustomer = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load customer details
      const customerRes = await fetch(`${API_URL}/api/customers/${customerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (customerRes.ok) {
        const customerData = await customerRes.json();
        setCustomer(customerData);
        setFormData({
          email: customerData.email || '',
          first_name: customerData.first_name || '',
          last_name: customerData.last_name || '',
          phone: customerData.phone || '',
          address: customerData.address || '',
          city: customerData.city || '',
          postal_code: customerData.postal_code || '',
          tags: customerData.tags || [],
          notes: customerData.notes || '',
          marketing_consent: customerData.marketing_consent || false,
          client_type: customerData.client_type || 'particulier',
          company_name: customerData.company_name || '',
          siret: customerData.siret || ''
        });

        // Load customer's reservations
        const resResponse = await fetch(`${API_URL}/api/reservations?client_email=${encodeURIComponent(customerData.email)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resResponse.ok) {
          const reservationsData = await resResponse.json();
          setReservations(Array.isArray(reservationsData) ? reservationsData : []);
        }

        // Load customer's invoices
        const invResponse = await fetch(`${API_URL}/api/invoices?client_email=${encodeURIComponent(customerData.email)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (invResponse.ok) {
          const invoicesData = await invResponse.json();
          setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
        }
      } else {
        toast.error('Client non trouvé');
        navigate('/dashboard/customers');
      }
    } catch (error) {
      console.error('Error loading customer:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.email) {
      toast.error('Email requis');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = customerId && customerId !== 'new' 
        ? `${API_URL}/api/customers/${customerId}`
        : `${API_URL}/api/customers`;
      
      const response = await fetch(url, {
        method: customerId && customerId !== 'new' ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(customerId && customerId !== 'new' ? 'Client mis à jour' : 'Client créé');
        setEditing(false);
        if (!customerId || customerId === 'new') {
          navigate(`/dashboard/customers/${data.id}`);
        } else {
          loadCustomer();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower.includes('pending') || statusLower.includes('attente')) {
      return <Badge className="bg-yellow-100 text-yellow-800">En attente</Badge>;
    }
    if (statusLower.includes('approved') || statusLower.includes('confirm') || statusLower.includes('valid')) {
      return <Badge className="bg-green-100 text-green-800">Acompte payé</Badge>;
    }
    if (statusLower.includes('completed') || statusLower.includes('terminée')) {
      return <Badge className="bg-blue-100 text-blue-800">Terminée</Badge>;
    }
    if (statusLower.includes('cancel') || statusLower.includes('annul')) {
      return <Badge className="bg-red-100 text-red-800">Annulée</Badge>;
    }
    return <Badge>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  const isEntreprise = formData.client_type === 'entreprise' || formData.client_type === 'association';
  const totalSpent = reservations.reduce((sum, r) => sum + (parseFloat(r.total_price || r.total_cost) || 0), 0);
  const paidInvoices = invoices.filter(i => i.status === 'paid').length;
  const isNewCustomer = !customerId || customerId === 'new';

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard/customers')}>
            <FiArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div className="border-l h-8" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {isNewCustomer ? 'Nouveau Client' : (
                isEntreprise && customer?.company_name 
                  ? customer.company_name 
                  : `${customer?.first_name} ${customer?.last_name}`
              )}
            </h1>
            {!isNewCustomer && (
              <p className="text-slate-500 text-sm">{customer?.email}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => { setEditing(false); if (isNewCustomer) navigate('/dashboard/customers'); }}>
                <FiX className="h-4 w-4 mr-2" />
                Annuler
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                <FiSave className="h-4 w-4 mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </>
          ) : (
            <Button onClick={() => setEditing(true)}>
              <FiEdit2 className="h-4 w-4 mr-2" />
              Modifier
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards (only for existing customers) */}
      {!isNewCustomer && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FiDollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-green-700">{formatCurrency(totalSpent)}</p>
                  <p className="text-xs text-green-600">CA Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FiCalendar className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-blue-700">{reservations.length}</p>
                  <p className="text-xs text-blue-600">Réservations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FiFileText className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-purple-700">{paidInvoices}/{invoices.length}</p>
                  <p className="text-xs text-purple-600">Factures payées</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`${customer?.marketing_consent ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${customer?.marketing_consent ? 'bg-green-100' : 'bg-slate-100'}`}>
                  <FiMail className={`h-5 w-5 ${customer?.marketing_consent ? 'text-green-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className={`text-xl font-bold ${customer?.marketing_consent ? 'text-green-700' : 'text-slate-400'}`}>
                    {customer?.marketing_consent ? 'Oui' : 'Non'}
                  </p>
                  <p className={`text-xs ${customer?.marketing_consent ? 'text-green-600' : 'text-slate-500'}`}>Marketing</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client Info */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {isEntreprise ? <FiBriefcase className="text-amber-600" /> : <FiUser className="text-indigo-600" />}
              Informations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Client Type */}
            {editing && (
              <div>
                <Label className="text-base font-medium">Type de client</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    type="button"
                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                      formData.client_type === 'particulier' 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setFormData({ ...formData, client_type: 'particulier' })}
                  >
                    <FiUser className={`h-5 w-5 ${formData.client_type === 'particulier' ? 'text-indigo-600' : 'text-slate-400'}`} />
                    <span className={`text-sm ${formData.client_type === 'particulier' ? 'text-indigo-700 font-medium' : 'text-slate-600'}`}>
                      Particulier
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                      isEntreprise ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setFormData({ ...formData, client_type: 'entreprise' })}
                  >
                    <FiBriefcase className={`h-5 w-5 ${isEntreprise ? 'text-amber-600' : 'text-slate-400'}`} />
                    <span className={`text-sm ${isEntreprise ? 'text-amber-700 font-medium' : 'text-slate-600'}`}>
                      Entreprise
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Company info for entreprise */}
            {isEntreprise && (
              <div className={`p-4 rounded-lg ${editing ? 'bg-amber-50' : 'bg-slate-50'} space-y-3`}>
                {editing ? (
                  <>
                    <div>
                      <Label>Nom de l'entreprise</Label>
                      <Input
                        value={formData.company_name}
                        onChange={e => setFormData({ ...formData, company_name: e.target.value })}
                        placeholder="Ma Société SAS"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>SIRET</Label>
                      <Input
                        value={formData.siret}
                        onChange={e => setFormData({ ...formData, siret: e.target.value })}
                        placeholder="123 456 789 00012"
                        className="mt-1 font-mono"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Entreprise</span>
                      <Badge className="bg-amber-100 text-amber-700">Entreprise</Badge>
                    </div>
                    {customer?.company_name && (
                      <p className="font-medium">{customer.company_name}</p>
                    )}
                    {customer?.siret && (
                      <p className="text-sm text-slate-500 font-mono">SIRET: {customer.siret}</p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom</Label>
                {editing ? (
                  <Input
                    value={formData.first_name}
                    onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                    className="mt-1"
                  />
                ) : (
                  <p className="mt-1 font-medium">{customer?.first_name || '-'}</p>
                )}
              </div>
              <div>
                <Label>Nom</Label>
                {editing ? (
                  <Input
                    value={formData.last_name}
                    onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                    className="mt-1"
                  />
                ) : (
                  <p className="mt-1 font-medium">{customer?.last_name || '-'}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <Label>Email</Label>
              {editing ? (
                <Input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1"
                />
              ) : (
                <div className="flex items-center gap-2 mt-1">
                  <FiMail className="h-4 w-4 text-slate-400" />
                  <a href={`mailto:${customer?.email}`} className="text-indigo-600 hover:underline">
                    {customer?.email}
                  </a>
                </div>
              )}
            </div>

            {/* Phone */}
            <div>
              <Label>Téléphone</Label>
              {editing ? (
                <Input
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1"
                />
              ) : (
                <div className="flex items-center gap-2 mt-1">
                  <FiPhone className="h-4 w-4 text-slate-400" />
                  {customer?.phone ? (
                    <a href={`tel:${customer.phone}`} className="text-indigo-600 hover:underline">
                      {customer.phone}
                    </a>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </div>
              )}
            </div>

            {/* Address */}
            <div>
              <Label>Adresse</Label>
              {editing ? (
                <>
                  <Input
                    value={formData.address}
                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Rue Example"
                    className="mt-1"
                  />
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Input
                      value={formData.postal_code}
                      onChange={e => setFormData({ ...formData, postal_code: e.target.value })}
                      placeholder="Code postal"
                    />
                    <Input
                      value={formData.city}
                      onChange={e => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Ville"
                    />
                  </div>
                </>
              ) : (
                <div className="flex items-start gap-2 mt-1">
                  <FiMapPin className="h-4 w-4 text-slate-400 mt-1" />
                  <div>
                    {customer?.address && <p>{customer.address}</p>}
                    {(customer?.postal_code || customer?.city) && (
                      <p className="text-slate-500">{customer?.postal_code} {customer?.city}</p>
                    )}
                    {!customer?.address && !customer?.city && <span className="text-slate-400">-</span>}
                  </div>
                </div>
              )}
            </div>

            {/* Marketing Consent */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Switch
                checked={formData.marketing_consent}
                onCheckedChange={v => setFormData({ ...formData, marketing_consent: v })}
                disabled={!editing}
              />
              <div>
                <p className="font-medium text-sm">Consentement marketing</p>
                <p className="text-xs text-slate-500">Accepte les emails promotionnels</p>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes internes</Label>
              {editing ? (
                <Textarea
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="mt-1"
                  placeholder="Notes sur ce client..."
                />
              ) : (
                <div className="mt-1 p-3 bg-slate-50 rounded-lg min-h-[60px]">
                  {customer?.notes ? (
                    <p className="text-sm whitespace-pre-wrap">{customer.notes}</p>
                  ) : (
                    <p className="text-sm text-slate-400 italic">Aucune note</p>
                  )}
                </div>
              )}
            </div>

            {/* Tags */}
            {customer?.tags?.length > 0 && (
              <div>
                <Label>Tags</Label>
                <div className="flex gap-2 flex-wrap mt-2">
                  {customer.tags.map(tag => (
                    <Badge key={tag} variant="secondary">{tag}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reservations & Invoices */}
        {!isNewCustomer && (
          <Card className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <CardHeader className="pb-0">
                <TabsList className="w-full">
                  <TabsTrigger value="reservations" className="flex-1">
                    <FiCalendar className="h-4 w-4 mr-2" />
                    Réservations ({reservations.length})
                  </TabsTrigger>
                  <TabsTrigger value="invoices" className="flex-1">
                    <FiFileText className="h-4 w-4 mr-2" />
                    Factures ({invoices.length})
                  </TabsTrigger>
                </TabsList>
              </CardHeader>

              <CardContent className="pt-4">
                <TabsContent value="reservations" className="mt-0">
                  {reservations.length === 0 ? (
                    <div className="text-center py-12 bg-slate-50 rounded-lg">
                      <FiCalendar className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                      <p className="text-slate-500">Aucune réservation</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {reservations.map((res) => (
                        <Link 
                          key={res.id} 
                          to={`/dashboard/reservations/${res.id}`}
                          className="block"
                        >
                          <div className="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer group">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{res.product_name || res.event_type || 'Événement'}</span>
                                {getStatusBadge(res.status)}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm text-slate-500">
                                <p><FiCalendar className="inline h-3 w-3 mr-1" /> {formatDate(res.start_date || res.event_date)}</p>
                                <p><FiMapPin className="inline h-3 w-3 mr-1" /> {res.event_address || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="font-bold">{formatCurrency(res.total_price || res.total_cost)}</p>
                                <p className="text-xs text-slate-500">
                                  {res.payment_status === 'paid' ? 'Payé' : res.deposit_paid ? 'Acompte payé' : 'En attente'}
                                </p>
                              </div>
                              <FiChevronRight className="h-5 w-5 text-slate-400 group-hover:text-slate-600" />
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="invoices" className="mt-0">
                  {invoices.length === 0 ? (
                    <div className="text-center py-12 bg-slate-50 rounded-lg">
                      <FiFileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                      <p className="text-slate-500">Aucune facture</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {invoices.map((inv) => (
                        <div key={inv.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{inv.invoice_number || `Facture`}</p>
                            <p className="text-sm text-slate-500">
                              {inv.type === 'deposit' ? 'Acompte' : 'Solde'} - {formatDate(inv.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-bold">{formatCurrency(inv.amount)}</p>
                              <Badge variant={inv.status === 'paid' ? 'default' : 'secondary'} className={inv.status === 'paid' ? 'bg-green-100 text-green-700' : ''}>
                                {inv.status === 'paid' ? 'Payée' : 'En attente'}
                              </Badge>
                            </div>
                            {inv.id && (
                              <a 
                                href={`${API_URL}/api/invoices/${inv.id}/pdf`} 
                                target="_blank" 
                                rel="noreferrer"
                                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                              >
                                <FiDownload className="h-5 w-5 text-slate-600" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        )}
      </div>
    </div>
  );
}
